/**
 * Bidirectional scroll synchronization between the CodeMirror source editor
 * and the rendered preview pane.
 *
 * After every preview render, `rebuild()` matches landmark source lines to
 * their rendered blocks (see scroll-map.ts). Scrolling either pane then maps
 * the scroll position through piecewise-linear interpolation between those
 * anchors and applies it to the other pane directly (no animation), so the
 * panes track each other continuously — including across regions where the
 * two sides have very different heights (collapsed code blocks, expanded
 * component embeds).
 *
 * Feedback loops are prevented with a scroll-ownership lock: whichever pane
 * the user is actively scrolling owns the sync for a short window, and the
 * echo scroll events fired by programmatic scrollTop writes on the other
 * pane are ignored.
 */

import type { EditorView } from '@codemirror/view';
import {
  extractSourceAnchors,
  interpolate,
  invertPairs,
  normalizeMatchText,
  referenceFromScroll,
  scrollFromReference,
  stripHeadingNumbering,
  toMonotonic,
  type MapPair,
  type SourceAnchor,
} from './scroll-map.js';

export interface ScrollSyncOptions {
  /** The preview scroll container (also holds the rendered blocks). */
  preview: HTMLElement;
  getEditorView: () => EditorView | null;
  /** Sync only runs when this returns true (edit mode on, rendered preview shown). */
  isEnabled: () => boolean;
  /** Substitute {{VAR}} values so source text matches the woven preview text. */
  resolveVariables: (text: string) => string;
}

interface MatchedAnchor {
  /** 1-based source line at the time of the last rebuild */
  line: number;
  el: HTMLElement;
}

interface PreviewBlock {
  el: HTMLElement;
  tag: string;
  /** Normalized text (headings have their numbering prefix stripped) */
  text: string;
  /** First non-empty code line, for PRE blocks */
  codeText: string | null;
}

/** How long the actively-scrolled pane keeps ownership after its last event. */
const OWNER_HOLD_MS = 150;

const TEXT_BLOCK_TAGS = new Set(['P', 'LI', 'BLOCKQUOTE']);

export class ScrollSync {
  /** null = no valid map (file switching, source view, weave error) */
  private anchors: MatchedAnchor[] | null = null;
  private owner: 'editor' | 'preview' | null = null;
  private ownerExpiry = 0;
  private editorRaf = 0;
  private previewRaf = 0;
  private pairsCache: {
    pairs: MapPair[];
    editorHeight: number;
    previewHeight: number;
    docLength: number;
  } | null = null;

  constructor(private readonly opts: ScrollSyncOptions) {
    opts.preview.addEventListener('scroll', () => this.onPreviewScroll(), { passive: true });
  }

  /** Drop the anchor map (file switched / preview no longer matches the editor doc). */
  invalidate(): void {
    this.anchors = null;
    this.pairsCache = null;
  }

  /**
   * Rebuild the source-line -> preview-block anchor map. Call after every
   * preview render, once the enrichers have run.
   */
  rebuild(source: string): void {
    this.pairsCache = null;
    const sourceAnchors = extractSourceAnchors(source, this.opts.resolveVariables);
    this.anchors = this.matchAnchors(sourceAnchors, this.collectPreviewBlocks());
  }

  /** Hook for the editor's DOM scroll event. */
  onEditorScroll(): void {
    if (!this.claim('editor')) return;
    if (this.editorRaf) return;
    this.editorRaf = requestAnimationFrame(() => {
      this.editorRaf = 0;
      this.syncFromEditor();
    });
  }

  /**
   * Scroll the preview so the block containing the editor's cursor sits at
   * the same vertical viewport position as the cursor line. Called after
   * typing-triggered re-renders so the section being edited is always in
   * view, even if free-scroll interpolation had drifted.
   */
  alignToCursor(): void {
    const view = this.opts.getEditorView();
    if (!view || !this.canSync()) return;
    const pairs = this.buildPairs(view);
    if (pairs.length === 0) return;

    const block = view.lineBlockAt(view.state.selection.main.head);
    const viewportOffset = block.top - view.scrollDOM.scrollTop;
    const previewTop = interpolate(pairs, block.top);

    const preview = this.opts.preview;
    const maxScroll = Math.max(0, preview.scrollHeight - preview.clientHeight);
    const target = Math.min(maxScroll, Math.max(0, previewTop - viewportOffset));

    this.claim('editor', true); // swallow the echo scroll event on the preview
    preview.scrollTop = target;
  }

  // --- scroll handling ---

  private onPreviewScroll(): void {
    if (!this.claim('preview')) return;
    if (this.previewRaf) return;
    this.previewRaf = requestAnimationFrame(() => {
      this.previewRaf = 0;
      this.syncFromPreview();
    });
  }

  /**
   * Take (or keep) scroll ownership for `side`. Returns false while the
   * other pane owns the sync — which is exactly the echo case: programmatic
   * scrollTop writes fire scroll events on the passive pane.
   */
  private claim(side: 'editor' | 'preview', force = false): boolean {
    const now = performance.now();
    if (!force && this.owner && this.owner !== side && now < this.ownerExpiry) return false;
    this.owner = side;
    this.ownerExpiry = now + OWNER_HOLD_MS;
    return true;
  }

  private canSync(): boolean {
    return (
      this.opts.isEnabled() &&
      this.anchors !== null &&
      this.opts.preview.clientHeight > 0
    );
  }

  private syncFromEditor(): void {
    const view = this.opts.getEditorView();
    if (!view || !this.canSync()) return;
    const pairs = this.buildPairs(view);
    if (pairs.length === 0) return;
    const scroller = view.scrollDOM;
    const preview = this.opts.preview;
    const ref = referenceFromScroll(scroller.scrollTop, scroller.scrollHeight, scroller.clientHeight);
    preview.scrollTop = scrollFromReference(
      interpolate(pairs, ref),
      preview.scrollHeight,
      preview.clientHeight
    );
  }

  private syncFromPreview(): void {
    const view = this.opts.getEditorView();
    if (!view || !this.canSync()) return;
    const pairs = this.buildPairs(view);
    if (pairs.length === 0) return;
    const scroller = view.scrollDOM;
    const preview = this.opts.preview;
    const ref = referenceFromScroll(preview.scrollTop, preview.scrollHeight, preview.clientHeight);
    scroller.scrollTop = scrollFromReference(
      interpolate(invertPairs(pairs), ref),
      scroller.scrollHeight,
      scroller.clientHeight
    );
  }

  // --- mapping ---

  /**
   * Resolve the anchor map to pixel pairs (editor content offset -> preview
   * content offset). Cached until either pane's content height or the editor
   * document changes (collapse toggles, image loads, edits).
   */
  private buildPairs(view: EditorView): MapPair[] {
    if (!this.anchors) return [];
    const preview = this.opts.preview;
    const scroller = view.scrollDOM;
    const editorHeight = scroller.scrollHeight;
    const previewHeight = preview.scrollHeight;
    const docLength = view.state.doc.length;

    const cached = this.pairsCache;
    if (
      cached &&
      cached.editorHeight === editorHeight &&
      cached.previewHeight === previewHeight &&
      cached.docLength === docLength
    ) {
      return cached.pairs;
    }

    const doc = view.state.doc;
    const previewRect = preview.getBoundingClientRect();
    const previewScrollTop = preview.scrollTop;

    const pairs: MapPair[] = [];
    for (const { line, el } of this.anchors) {
      // Edits since the last rebuild may have shifted/removed lines; skip
      // anchors that no longer resolve (the map refreshes on next render).
      if (line > doc.lines || !el.isConnected) continue;
      pairs.push({
        a: view.lineBlockAt(doc.line(line).from).top,
        b: el.getBoundingClientRect().top - previewRect.top + previewScrollTop,
      });
    }

    // Endpoints: document start and full content heights. The map operates in
    // content space; scrollTop conversion happens via the sliding viewport
    // reference point (see referenceFromScroll).
    pairs.push({ a: 0, b: 0 });
    pairs.push({ a: editorHeight, b: previewHeight });
    pairs.sort((p, q) => p.a - q.a || p.b - q.b);

    const mono = toMonotonic(pairs);
    this.pairsCache = { pairs: mono, editorHeight, previewHeight, docLength };
    return mono;
  }

  // --- anchor matching ---

  private collectPreviewBlocks(): PreviewBlock[] {
    const els = this.opts.preview.querySelectorAll<HTMLElement>(
      'h1,h2,h3,h4,h5,h6,p,li,pre,blockquote'
    );
    const blocks: PreviewBlock[] = [];
    for (const el of els) {
      // Skip generated navigation ([ld:toc] nav) — its entries duplicate
      // heading texts and would fool the matcher.
      if (el.closest('nav')) continue;
      const tag = el.tagName;
      if (tag === 'PRE') {
        const raw = el.querySelector('code')?.textContent ?? '';
        const firstLine = raw.split('\n').find(l => l.trim());
        blocks.push({
          el,
          tag,
          text: '',
          codeText: firstLine ? normalizeMatchText(firstLine) : null,
        });
      } else {
        const raw = el.textContent ?? '';
        const isHeading = tag.length === 2 && tag[0] === 'H';
        const text = normalizeMatchText(isHeading ? stripHeadingNumbering(raw.trim()) : raw);
        blocks.push({ el, tag, text: text.slice(0, 200), codeText: null });
      }
    }
    return blocks;
  }

  /**
   * Match source anchors to preview blocks monotonically: both sides are in
   * document order, so each anchor searches forward from the previous match.
   * Preview-only content (expanded embeds) is skipped over; source anchors
   * with no counterpart are dropped.
   */
  private matchAnchors(sourceAnchors: SourceAnchor[], blocks: PreviewBlock[]): MatchedAnchor[] {
    const matched: MatchedAnchor[] = [];
    let cursor = 0;
    for (const anchor of sourceAnchors) {
      for (let k = cursor; k < blocks.length; k++) {
        if (!this.blockMatches(anchor, blocks[k])) continue;
        matched.push({ line: anchor.line, el: blocks[k].el });
        cursor = k + 1;
        break;
      }
    }
    return matched;
  }

  private blockMatches(anchor: SourceAnchor, block: PreviewBlock): boolean {
    switch (anchor.kind) {
      case 'heading':
        return block.tag === `H${anchor.level}` && block.text === anchor.text;
      case 'code':
        return block.tag === 'PRE' && block.codeText === anchor.text;
      case 'text':
        return TEXT_BLOCK_TAGS.has(block.tag) && block.text.startsWith(anchor.text);
    }
  }
}
